'use client'

import type { ErrandResponse } from '@backend/schemas/src/errands'
import { format } from 'date-fns'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useDeleteErrand } from '@/api/hooks/useDeleteErrand'
import { errandDeleteToast } from '@/components/molecules/custom-async-toasts'
import { ErrandStatusLabel } from '@/components/molecules/errand-status-label'
import { DropdownMenuItem } from '@/components/ui/dropdown-menu'
import { useIndividuals } from '@/hooks/useIndividuals'
import { DateUtils } from '@/lib/utils'
import { ConfirmDialog } from '../confirm-dialog'
import type { CustomColumnDef } from '../data-table'
import { ColumnActions } from '../data-table/actions-column'

export const useErrandsTable: (
  errands: ErrandResponse[],
  onOpenErrand: (id: string) => void,
) => CustomColumnDef<ErrandResponse>[] = (_errands, onOpenErrand) => {
  const { selectedIndividualId } = useIndividuals()
  const deleteErrand = useDeleteErrand()

  const { t } = useTranslation()

  const onRequestDelete = (id: string) => {
    if (!selectedIndividualId) return
    const deletionPromise = deleteErrand.mutateAsync({
      errandId: id,
      individualId: selectedIndividualId,
    })
    errandDeleteToast(deletionPromise)
  }

  return [
    {
      cell: ({
        row: {
          original: { status, title, updatedAt },
        },
      }) => {
        return (
          <div>
            <p className="max-w-prose">{title}</p>
            <div className="@3xl/table:hidden mt-2">
              <ErrandStatusLabel status={status} />
            </div>
            <div className="@3xl/table:hidden mt-2 text-xs text-text-secondary font-medium">
              {DateUtils.formatRelativeDate(new Date(updatedAt), t)}
            </div>
          </div>
        )
      },
      header: t('errandsTable.columns.errand'),
      id: 'messageSummary',
      size: Number.POSITIVE_INFINITY,
    },
    {
      accessorKey: 'status',
      cell: ({ cell }) => {
        const status = cell.getValue<ErrandResponse['status']>()

        return (
          <div className="flex justify-center">
            <ErrandStatusLabel status={status} />
          </div>
        )
      },
      columnClassName: 'hidden @3xl/table:table-cell',
      header: () => <div className="text-center">{t('errandsTable.columns.status')}</div>,
      id: 'status',
    },
    {
      accessorKey: 'updatedAt',
      cell: ({ cell }) => {
        const date = cell.getValue<string>() ? new Date(cell.getValue<string>()) : new Date()

        return (
          <div className="text-text-secondary text-right">
            {DateUtils.formatRelativeDate(new Date(date), t)}
          </div>
        )
      },
      columnClassName: 'hidden @3xl/table:table-cell',
      header: () => <div className="text-right">{t('global.updatedAt')}</div>,
      id: 'updatedAt',
    },
    {
      cell: ({ row }) => {
        const [deleteOpen, setDeleteOpen] = useState(false)

        return (
          <ColumnActions
            dropDownMenuContent={
              <DropdownMenuItem destructive onSelect={() => setDeleteOpen(true)}>
                {t('errandsTable.rowActionsDropdown.deleteErrand')}
              </DropdownMenuItem>
            }
            onClickableButtonClick={() => onOpenErrand(row.original.id)}
          >
            <ConfirmDialog
              onConfirm={() => onRequestDelete(row.original.id)}
              onOpenChange={setDeleteOpen}
              open={deleteOpen}
              variant="destructive"
            />
          </ColumnActions>
        )
      },
      columnClassName: 'w-px p-2 @3xl/table:p-5',
      enableHiding: false,
      id: 'actions',
    },
  ] satisfies CustomColumnDef<ErrandResponse>[]
}
